// Calendrier d'entrainement (PlanningController cote serveur).

export type SeancePlanifiee = {
  id: string;
  idSeance?: string | null;
  date: string; // AAAA-MM-JJ
  ordre: number;
  nom: string;
  notation: string;
  dureeTotaleS?: number | null;
  distanceTotaleM?: number | null;
  chargeUA?: number | null;
  rpe?: number | null;
  zonePrincipale?: string | null;
  typeSeance?: string | null;
  // Composition (modele de seance applique a la planification) : notation
  // contient echauffement + corps + recuperation ; chaque partie a part.
  // CORPS, SEANCE, COMPETITION, REPOS ou NOTE (null pour les seances posees avant).
  // NOTE : titre dans nom, consigne dans notes.
  categorie?: string | null;
  // Note libre (repos, competition).
  notes?: string | null;
  // Competition posee depuis la liste des courses : course d'origine.
  idCourse?: string | null;
  // Lien vers le site de la course (competition posee depuis l'Agenda).
  lienCourse?: string | null;
  // Envoi vers intervals.icu : evenement cree et date du dernier envoi.
  icuEventId?: string | null;
  dateEnvoiIcu?: string | null;
  // Renseigne si la mise a jour d'intervals.icu a echoue (deplacement, note).
  avertissementIcu?: string | null;
  // Recomposee (changement de semaine d'assimilation / d'affutage) : a
  // renvoyer a intervals.icu si elle y avait ete envoyee.
  recomposee?: boolean;
  nomModele?: string | null;
  notationEchauffement?: string | null;
  notationCorps?: string | null;
  notationRecuperation?: string | null;
  // Seance d'origine qui ne correspond plus (aujourd'hui et jours a venir) ;
  // null si elle correspond ou sans objet.
  etatOrigine?: EtatOrigine | null;
};

export type EtatOrigine = 'SUPPRIMEE' | 'MODIFIEE' | 'INACCESSIBLE';

/** Explication affichee pour une seance posee dont l'origine ne correspond plus. */
export function libelleOrigine(etat?: EtatOrigine | null): string | null {
  switch (etat) {
    case 'SUPPRIMEE': return "La séance d'origine a été supprimée.";
    case 'MODIFIEE': return "La séance d'origine a été modifiée depuis qu'elle a été posée.";
    case 'INACCESSIBLE': return "La séance d'origine ne t'est plus accessible (retirée de la bibliothèque commune ?).";
    default: return null;
  }
}

function entetes(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
  };
}

async function verifier(response: Response, attendus: number[]): Promise<Response> {
  if (attendus.includes(response.status)) return response;
  let message = 'statut ' + response.status;
  if (response.status === 404) message = 'séance introuvable';
  try {
    const erreur = await response.json();
    if (erreur?.message) message = erreur.message;
  } catch {
    // corps vide ou non JSON
  }
  throw new Error(message);
}

/**
 * Calendrier vise : celui de l'utilisateur (null), ou (coach) celui d'un
 * athlete rattache (son id SSO), passe en parametre "athlete" de l'adresse.
 */
export type Cible = string | null | undefined;

export function avecAthlete(url: string, athlete: Cible): string {
  return athlete ? `${url}${url.includes('?') ? '&' : '?'}athlete=${encodeURIComponent(athlete)}` : url;
}

export async function listerPlanning(debut: string, fin: string, athlete?: Cible): Promise<SeancePlanifiee[]> {
  const url = avecAthlete(`/api/planning?debut=${encodeURIComponent(debut)}&fin=${encodeURIComponent(fin)}`, athlete);
  const response = await verifier(await fetch(url, { headers: entetes() }), [200]);
  return (await response.json()) as SeancePlanifiee[];
}

export async function planifierSeance(idSeance: string, date: string, athlete?: Cible): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning', athlete), { method: 'POST', headers: entetes(), body: JSON.stringify({ idSeance, date }) }),
    [201],
  );
  return (await response.json()) as SeancePlanifiee;
}

/** Pose un repos a une date, avec une note facultative. */
export async function planifierRepos(date: string, notes?: string | null, athlete?: Cible): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning', athlete), { method: 'POST', headers: entetes(), body: JSON.stringify({ categorie: 'REPOS', date, notes }) }),
    [201],
  );
  return (await response.json()) as SeancePlanifiee;
}

/** Pose une note (titre facultatif, consigne obligatoire) a une date. */
export async function planifierNote(date: string, nom: string, notes: string, athlete?: Cible): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning', athlete), { method: 'POST', headers: entetes(), body: JSON.stringify({ categorie: 'NOTE', date, nom, notes }) }),
    [201],
  );
  return (await response.json()) as SeancePlanifiee;
}

/** Repos ou note : simple note de journee, sans seance a realiser. */
export function estNoteJournee(categorie?: string | null): boolean {
  return categorie === 'REPOS' || categorie === 'NOTE';
}

/** Pose une competition choisie dans la liste des courses (synthese calculee par le front). */
export async function planifierCourse(course: {
  date: string;
  nom: string;
  notation: string;
  idCourse?: string | null;
  notes?: string | null;
  dureeTotaleS?: number | null;
  distanceTotaleM?: number | null;
  chargeUA?: number | null;
  rpe?: number | null;
  zonePrincipale?: string | null;
}): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch('/api/planning', { method: 'POST', headers: entetes(), body: JSON.stringify({ ...course, categorie: 'COMPETITION' }) }),
    [201],
  );
  return (await response.json()) as SeancePlanifiee;
}

/**
 * Modifie la note d'une seance planifiee ou d'un repos (vide = aucune note),
 * ou la consigne et le titre (nom) d'une note.
 */
export async function modifierNotes(id: string, notes: string, athlete?: Cible, nom?: string): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/' + encodeURIComponent(id) + '/notes', athlete), { method: 'PUT', headers: entetes(), body: JSON.stringify({ notes, nom }) }),
    [200],
  );
  return (await response.json()) as SeancePlanifiee;
}

export async function deplacerSeance(id: string, date: string, athlete?: Cible): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/' + encodeURIComponent(id), athlete), { method: 'PUT', headers: entetes(), body: JSON.stringify({ date }) }),
    [200],
  );
  return (await response.json()) as SeancePlanifiee;
}

/**
 * Remet une seance posee a jour depuis sa seance d'origine (modifiee depuis
 * la pose) ; recomposee : a renvoyer a intervals.icu si elle y avait ete envoyee.
 */
export async function actualiserSeance(id: string, athlete?: Cible): Promise<SeancePlanifiee> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/' + encodeURIComponent(id) + '/actualiser', athlete), { method: 'POST', headers: entetes() }),
    [200],
  );
  return (await response.json()) as SeancePlanifiee;
}

/**
 * Retire une seance planifiee (et son evenement intervals.icu si elle y a ete
 * envoyee). Renvoie un avertissement si intervals.icu n'a pas pu etre mis a jour.
 */
export async function retirerSeance(id: string, athlete?: Cible): Promise<string | null> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/' + encodeURIComponent(id), athlete), { method: 'DELETE', headers: entetes() }),
    [200, 204],
  );
  if (response.status === 204) return null;
  const corps = (await response.json()) as { avertissement?: string };
  return corps.avertissement ?? null;
}

/** Semaine d'assimilation ou d'affutage (les autres sont normales). */
export type TypeSemaine = 'ASSIMILATION' | 'AFFUTAGE';
export type Semaine = { lundi: string; type: TypeSemaine | null; recomposees?: SeancePlanifiee[] };

export const TYPES_SEMAINE: { code: TypeSemaine; libelle: string; couleur: string; fond: string; description: string }[] = [
  {
    code: 'ASSIMILATION',
    libelle: 'Assimilation',
    couleur: '#00838f',
    fond: 'rgba(0, 131, 143, 0.07)',
    description: 'Semaine allégée pour assimiler la charge des semaines précédentes',
  },
  {
    code: 'AFFUTAGE',
    libelle: 'Affûtage',
    couleur: '#6a1b9a',
    fond: 'rgba(106, 27, 154, 0.07)',
    description: 'Semaine(s) précédant une compétition',
  },
];

export function infoSemaine(type?: string | null) {
  return TYPES_SEMAINE.find((t) => t.code === type) ?? null;
}

export async function listerSemaines(debut: string, fin: string, athlete?: Cible): Promise<Semaine[]> {
  const response = await verifier(
    await fetch(avecAthlete(`/api/planning/semaines?debut=${debut}&fin=${fin}`, athlete), { headers: entetes() }),
    [200],
  );
  return (await response.json()) as Semaine[];
}

/**
 * Type de la semaine d'une date (null = normale). Les repetitions deja posees
 * sont recomposees avec le modele d'assimilation / d'affutage (ou l'inverse).
 */
export async function definirSemaine(date: string, type: TypeSemaine | null, athlete?: Cible): Promise<Semaine> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/semaines/' + date, athlete), { method: 'PUT', headers: entetes(), body: JSON.stringify({ type: type ?? 'NORMALE' }) }),
    [200],
  );
  return (await response.json()) as Semaine;
}

/**
 * Decompte de semaines : SN (lundiDebut) ... S1 (lundiCible) ; la couleur de
 * chaque pastille suit le type de la semaine (normale, assimilation, affutage).
 * Plusieurs decomptes par calendrier, sans chevauchement.
 */
export type Decompte = { id: string; lundiDebut: string; lundiCible: string };

export async function listerDecomptes(athlete?: Cible): Promise<Decompte[]> {
  const response = await verifier(await fetch(avecAthlete('/api/planning/decomptes', athlete), { headers: entetes() }), [200]);
  return (await response.json()) as Decompte[];
}

/** Pose un decompte ; erreur (409) s'il en chevauche un autre. */
export async function poserDecompte(decompte: { lundiCible: string; lundiDebut: string }, athlete?: Cible): Promise<Decompte> {
  const response = await verifier(
    await fetch(avecAthlete('/api/planning/decomptes', athlete), { method: 'POST', headers: entetes(), body: JSON.stringify(decompte) }),
    [201],
  );
  return (await response.json()) as Decompte;
}

export async function supprimerDecompte(id: string, athlete?: Cible): Promise<void> {
  await verifier(await fetch(avecAthlete('/api/planning/decomptes/' + encodeURIComponent(id), athlete), { method: 'DELETE', headers: entetes() }), [204]);
}
